import { g } from "analytics";
import { AddArrow, DeleteArrows } from "arrows/command";
import { AddApparatus, DeleteApparatus, Deselect } from "command/basic";
import { commandProcessor, Sequence } from "command/command";
import { GroupObjects } from "editing/grouping/commands";
import { getRootGroup } from "editing/grouping/operations";
import { Arrays } from "ext/array";
import { ID } from "store/id";
import store from "store/store";
import { add } from "types";
/** Distance to paste each copy from the previous. */
export const DELTA = 50;
/** Handles clipboard operations for apparatus and other items. */
class Clipboard {
    constructor() {
        /**
         * The contents of the clipboard, i.e. what to create when pasting.
         */
        this.contents = {
            apparatusNotInGroup: [],
            arrowsNotInGroup: [],
            groups: []
        };
        // Number of DELTA steps to paste the new items from the original.
        this.deltaSteps = 0;
        this.createApparatusData = (a) => {
            return Object.assign(Object.assign({}, a), { id: ID.mint("apparatus"), x: a.x + DELTA * this.deltaSteps, y: a.y + DELTA * this.deltaSteps, parentGroup: undefined });
        };
        this.createArrow = (a) => {
            return Object.assign(Object.assign({}, a), { id: ID.mint("arrow"), start: add(a.start, { x: DELTA * this.deltaSteps, y: DELTA * this.deltaSteps }), end: add(a.end, { x: DELTA * this.deltaSteps, y: DELTA * this.deltaSteps }), parentGroup: undefined });
        };
    }
    /**
     * Inspects the current store state and stores selected items in the clipboard
     * Returns true if there were selected items, false otherwise.
     */
    fillContentsFromStore() {
        if (store.selectedArrows().length + store.selectedItems().length == 0)
            return false;
        const newContents = {
            apparatusNotInGroup: [],
            arrowsNotInGroup: [],
            groups: []
        };
        // Track groups already added.
        let alreadyAddedRootGroupIds = [];
        for (const apparatus of store.selectedItems()) {
            if (apparatus.parentGroup) {
                const rootGroup = getRootGroup(apparatus.parentGroup);
                if (!Arrays.includes(alreadyAddedRootGroupIds, rootGroup.id)) {
                    newContents.groups.push(this.templatizeGroup(rootGroup));
                    alreadyAddedRootGroupIds.push(rootGroup.id);
                }
            }
            else {
                newContents.apparatusNotInGroup.push(apparatus);
            }
        }
        for (const arrow of store.selectedArrows()) {
            if (arrow.parentGroup) {
                const rootGroup = getRootGroup(arrow.parentGroup);
                if (!Arrays.includes(alreadyAddedRootGroupIds, rootGroup.id)) {
                    newContents.groups.push(this.templatizeGroup(rootGroup));
                    alreadyAddedRootGroupIds.push(rootGroup.id);
                }
            }
            else {
                newContents.arrowsNotInGroup.push(arrow);
            }
        }
        this.contents = newContents;
        return true;
    }
    /** Turn a group into a templated group. */
    templatizeGroup(group) {
        return {
            items: group.items.map(childId => {
                const idf = ID.matching(childId);
                switch (childId.type) {
                    case "apparatus":
                        return { type: "apparatus", data: store.selectedItems().filter(idf)[0] };
                    case "arrow":
                        return { type: "arrow", data: store.selectedArrows().filter(idf)[0] };
                    case "group":
                        return { type: "group", data: this.templatizeGroup(store.groups().filter(idf)[0]) };
                }
            })
        };
    }
    cut() {
        if (!this.fillContentsFromStore())
            return;
        this.deltaSteps = 0;
        commandProcessor.execute(new Sequence([
            new DeleteApparatus(store.selectedItems()),
            new DeleteArrows(store.selectedArrows()),
        ]));
    }
    copy() {
        if (!this.fillContentsFromStore())
            return;
        this.deltaSteps = 1;
    }
    paste(opts) {
        if ((opts === null || opts === void 0 ? void 0 : opts.inPlace) === true) {
            // Remove a step, but don't go below 0.
            this.deltaSteps = Math.max(0, this.deltaSteps - 1);
        }
        commandProcessor.execute(new Sequence([
            new AddApparatus(this.contents.apparatusNotInGroup.map(this.createApparatusData)),
            new AddArrow(this.contents.arrowsNotInGroup.map(this.createArrow)),
            ...flatten(this.contents.groups.map(g => this.createPasteCommandsForGroup(g))),
            // Deselect currently selected items
            new Deselect([...store.selectedItems().map(a => a.id), ...store.selectedArrows().map(a => a.id)]),
        ]));
        this.deltaSteps++;
    }
    createPasteCommandsForGroup(group, groupId) {
        var commands = [];
        var childIds = [];
        for (const child of group.items) {
            switch (child.type) {
                case "apparatus":
                    const apparatus = this.createApparatusData(child.data);
                    childIds.push(apparatus.id);
                    commands.push(new AddApparatus([apparatus]));
                    break;
                case "arrow":
                    const arrow = this.createArrow(child.data);
                    childIds.push(arrow.id);
                    commands.push(new AddArrow([arrow]));
                    break;
                case "group":
                    const childGroupId = ID.mint("group");
                    childIds.push(childGroupId);
                    commands.push(...this.createPasteCommandsForGroup(child.data, childGroupId));
                    break;
            }
        }
        commands.push(new GroupObjects(childIds, groupId));
        return commands;
    }
}
function flatten(commands) {
    return commands.reduce((acc, els) => { acc.push(...els); return acc; }, []);
}
/** Clipboard that also logs events to Google Analytics. */
class LoggingClipboard {
    constructor() {
        this.clipboard = new Clipboard();
    }
    cut(source) {
        g("Cut", source);
        this.clipboard.cut();
    }
    copy(source) {
        g("Copy", source);
        this.clipboard.copy();
    }
    paste(source, opts) {
        var _a;
        const inPlace = (_a = opts === null || opts === void 0 ? void 0 : opts.inPlace) !== null && _a !== void 0 ? _a : false;
        g(inPlace ? "Paste::InPlace" : "Paste", source);
        if (inPlace) {
            // toastr.info("Item pasted in place. It will appear on top of the original item")
        }
        this.clipboard.paste(opts);
    }
}
export default new LoggingClipboard();
